import { Directive, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import {
  DialogGeometry, DialogResizeEdge, geometryForResize,
  DEFAULT_DIALOG_MIN_WIDTH, DEFAULT_DIALOG_MIN_HEIGHT
} from './is-dialog-geometry';

/** Um handle por extremidade/canto. Usa Pointer Events e pointer capture. */
@Directive({ selector: '[appResize]' })
export class ResizeDirective implements OnDestroy {
  @Input() resizeEdge: DialogResizeEdge = 'bottom-right';
  @Input() resizeRect!: DialogGeometry;
  @Input() resizeDisabled = false;
  @Input() resizeMinWidth = DEFAULT_DIALOG_MIN_WIDTH;
  @Input() resizeMinHeight = DEFAULT_DIALOG_MIN_HEIGHT;

  @Output() resizeStart = new EventEmitter<void>();
  @Output() resizeRectChange = new EventEmitter<DialogGeometry>();
  @Output() resizeEnd = new EventEmitter<void>();

  private activePointer: number | null = null;
  private startX = 0;
  private startY = 0;
  private initial!: DialogGeometry;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (this.resizeDisabled || this.activePointer !== null || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation(); // Impede que o CdkDrag da janela inicie um drag.
    this.activePointer = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.initial = { ...this.resizeRect };
    this.elementRef.nativeElement.setPointerCapture(event.pointerId);
    this.resizeStart.emit();
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.activePointer) return;
    event.preventDefault();
    const next = geometryForResize(
      this.initial, this.resizeEdge,
      event.clientX - this.startX, event.clientY - this.startY,
      { width: window.innerWidth, height: window.innerHeight },
      this.resizeMinWidth, this.resizeMinHeight
    );
    this.resizeRectChange.emit(next);
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerEnd(event: PointerEvent): void {
    if (event.pointerId !== this.activePointer) return;
    this.finish();
  }

  ngOnDestroy(): void { this.finish(); }

  private finish(): void {
    if (this.activePointer === null) return;
    const pointerId = this.activePointer;
    this.activePointer = null;
    const handle = this.elementRef.nativeElement;
    if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId);
    this.resizeEnd.emit();
  }
}
